import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Minus, Plus, RotateCcw, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import pdfWorkerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import PdfJsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?worker";
import { usePdfViewer, registerPdfViewerHandle } from "@/contexts/PdfViewerContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { SHEET_SAFE_PB_CLASS } from "@/components/ui/sheetChrome";
import { isDespiaNative } from "@/lib/isDespiaNative";
import { supabase } from "@/integrations/supabase/client";
import despia from "despia-native";

type PdfPageMeta = {
  pageNumber: number;
  /** Intrinsic CSS-px width at zoom = 1 (fit-to-width). */
  baseWidth: number;
  /** Intrinsic CSS-px height at zoom = 1. */
  baseHeight: number;
  /** PDF.js scale that yields baseWidth at zoom = 1. */
  fitScale: number;
};

const MIN_ZOOM = 0.75;
const MAX_ZOOM = 5;
const MAX_RENDER_SCALE = 6; // hard cap on PDF.js scale to avoid huge canvases
const RENDER_DEBOUNCE_MS = 160;

/** iOS-style share glyph. */
function IosShareIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M12 3v12" />
      <path d="M8 7l4-4 4 4" />
      <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    </svg>
  );
}

/**
 * Per-page <canvas> that re-renders with PDF.js whenever `zoom` changes
 * (debounced). The canvas backing store is sized so 1 CSS-px corresponds to
 * `dpr * zoom` device pixels — keeps text vector-crisp at any zoom level.
 */
function PdfPageCanvas({
  pdfPage,
  meta,
  zoom,
  dpr,
}: {
  pdfPage: any;
  meta: PdfPageMeta;
  zoom: number;
  dpr: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const renderTaskRef = useRef<{ cancel: () => void } | null>(null);
  const lastScaleRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Effective PDF.js scale: fit-to-width × device pixels × zoom.
    const targetScale = Math.min(
      meta.fitScale * dpr * Math.max(zoom, 0.5),
      MAX_RENDER_SCALE,
    );

    // Avoid pointless re-renders when scale barely changes.
    if (Math.abs(targetScale - lastScaleRef.current) < 0.01) return;

    let cancelled = false;
    const handle = window.setTimeout(async () => {
      try {
        const viewport = pdfPage.getViewport({ scale: targetScale });
        if (cancelled) return;

        // Cancel any in-flight render before starting a new one.
        try { renderTaskRef.current?.cancel(); } catch { /* ignore */ }

        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        const ctx = canvas.getContext("2d", { alpha: false });
        if (!ctx) return;

        const task = pdfPage.render({ canvas, canvasContext: ctx, viewport });
        renderTaskRef.current = task;
        await task.promise;
        if (!cancelled) lastScaleRef.current = targetScale;
      } catch (err: any) {
        if (err?.name !== "RenderingCancelledException") {
          console.warn("[PdfPageCanvas] render failed:", err);
        }
      }
    }, RENDER_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
      try { renderTaskRef.current?.cancel(); } catch { /* ignore */ }
    };
  }, [pdfPage, meta.fitScale, zoom, dpr]);

  return (
    <canvas
      ref={canvasRef}
      className="block mx-auto my-3 bg-white shadow-2xl rounded-md select-none"
      style={{
        width: meta.baseWidth * zoom,
        height: meta.baseHeight * zoom,
      }}
    />
  );
}

/**
 * Premium full-screen in-app PDF viewer.
 *
 * Mobile/tablet: PDF.js renders each page into its own <canvas>, and pages are
 * re-rendered at the active zoom level so text stays crisp at any scale.
 * Supports pinch-to-zoom with live CSS transform during the gesture and a
 * pixel-accurate re-render on release. Buttons +/- and double-tap also work.
 *
 * Desktop: keeps a native <iframe> to leverage the browser's PDF plugin.
 */
export function PdfViewerOverlay() {
  const { t } = useTranslation();
  const { isOpen, payload, open, close } = usePdfViewer();
  const isMobile = useIsMobile();
  // Use the in-app pdf.js viewer (pinch-zoom + share) on every touch / native
  // surface — not just narrow widths. Otherwise an iPad in landscape (~1366px)
  // or any wide native (Despia WebView) screen falls back to a bare <iframe>,
  // which renders blank inside the WebView ("the PDF won't open"). The iframe
  // path is reserved for true desktop browsers with a fine pointer.
  /**
   * ¿PDF o foto? Las facturas escaneadas son las dos cosas —hay `.jpg` guardados
   * junto a los `.pdf`—, y una foto por PDF.js no se abre: sale «no se pudo
   * mostrar el PDF» encima de una imagen que se ve perfectamente.
   *
   * Manda lo que diga quien abre el visor; si no dice nada, la extensión.
   */
  const isImage = useMemo(() => {
    if (!payload) return false;
    if (payload.kind) return payload.kind === "image";
    return /\.(jpe?g|png|webp|gif|heic|heif|avif)(\?|#|$)/i.test(payload.filename || payload.url);
  }, [payload]);

  const preferCanvasForPdf = useMemo(() => {
    if (isDespiaNative()) return true;
    if (isMobile) return true;
    if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
      return window.matchMedia("(pointer: coarse)").matches;
    }
    return false;
  }, [isMobile]);

  /* La foto SIEMPRE por el visor propio: el `<iframe>` del escritorio la
     enseñaría a pelo, sin zoom y sobre el fondo blanco del navegador. */
  const preferCanvas = preferCanvasForPdf || isImage;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [rendering, setRendering] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [pages, setPages] = useState<PdfPageMeta[]>([]);
  const pdfPagesRef = useRef<any[]>([]);
  const [zoom, setZoom] = useState(1);
  const [imageReady, setImageReady] = useState(false);
  const [sharing, setSharing] = useState(false);
  const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 3) : 1;

  useEffect(() => {
    registerPdfViewerHandle({ open, close });
    return () => registerPdfViewerHandle(null);
  }, [open, close]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, close]);

  // Reset every time we open a new doc.
  useEffect(() => {
    if (isOpen) {
      setZoom(1);
      setPages([]);
      setImageReady(false);
      setRenderError(null);
      pdfPagesRef.current = [];
    }
  }, [isOpen, payload?.url]);

  // Load PDF + compute page metadata for the in-app canvas viewer.
  useEffect(() => {
    if (!isOpen || !payload || !preferCanvas) return;
    // Una foto no pasa por PDF.js: la pinta el navegador (ver más abajo).
    if (isImage) return;

    let cancelled = false;
    const renderTimeout = window.setTimeout(() => {
      if (!cancelled) {
        setRenderError(t("pdfViewer.renderError", { defaultValue: "Could not display PDF" }));
        setRendering(false);
      }
    }, 20_000);

    setRendering(true);
    setRenderError(null);

    (async () => {
      try {
        const pdfjsLib: any = await import("pdfjs-dist");
        // Prefer a bundled, same-origin module worker — robust inside the native
        // WebView, where fetching the worker by URL can be blocked/CSP-denied.
        // Set once (app lifetime). Fall back to the URL worker if the bundled
        // worker can't be constructed, so behaviour is never worse than before.
        try {
          if (!pdfjsLib.GlobalWorkerOptions.workerPort) {
            pdfjsLib.GlobalWorkerOptions.workerPort = new PdfJsWorker();
          }
        } catch {
          if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
            pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;
          }
        }

        const buf = payload.blob
          ? await payload.blob.arrayBuffer()
          : await (await fetch(payload.url)).arrayBuffer();
        if (cancelled) return;

        const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
        if (cancelled) return;

        const cssWidth = Math.max(
          320,
          (scrollRef.current?.clientWidth || window.innerWidth) - 16,
        );

        const metas: PdfPageMeta[] = [];
        const proxies: any[] = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) return;
          const page = await pdf.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const fitScale = cssWidth / base.width;
          proxies.push(page);
          metas.push({
            pageNumber: i,
            baseWidth: cssWidth,
            baseHeight: base.height * fitScale,
            fitScale,
          });
        }

        if (!cancelled) {
          pdfPagesRef.current = proxies;
          setPages(metas);
          window.clearTimeout(renderTimeout);
          setRendering(false);
        }
      } catch (err) {
        console.error("[PdfViewerOverlay] PDF.js load failed:", err);
        if (!cancelled) {
          window.clearTimeout(renderTimeout);
          setRenderError(t("pdfViewer.renderError", { defaultValue: "Could not display PDF" }));
          setRendering(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(renderTimeout);
    };
  }, [isOpen, payload, preferCanvas, isImage, t]);

  // ---------- Pinch-to-zoom gesture ----------
  const pinchRef = useRef<{
    active: boolean;
    startDist: number;
    startZoom: number;
    midX: number;
    midY: number;
    startScrollLeft: number;
    startScrollTop: number;
  } | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // iOS drives pinch via gesture* events (effect above) — skip the
    // touch-based pinch there so the two don't fight.
    if (typeof window !== "undefined" && "ongesturestart" in window) return;
    if (e.touches.length !== 2) return;
    const [a, b] = [e.touches[0], e.touches[1]];
    const dx = a.clientX - b.clientX;
    const dy = a.clientY - b.clientY;
    const dist = Math.hypot(dx, dy);
    const scroll = scrollRef.current;
    if (!scroll) return;
    const rect = scroll.getBoundingClientRect();
    pinchRef.current = {
      active: true,
      startDist: dist,
      startZoom: zoom,
      midX: (a.clientX + b.clientX) / 2 - rect.left,
      midY: (a.clientY + b.clientY) / 2 - rect.top,
      startScrollLeft: scroll.scrollLeft,
      startScrollTop: scroll.scrollTop,
    };
    // Disable transitions; we'll apply transform directly.
    if (innerRef.current) {
      innerRef.current.style.transformOrigin = "0 0";
    }
  }, [zoom]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const p = pinchRef.current;
    if (!p || !p.active || e.touches.length !== 2) return;
    const [a, b] = [e.touches[0], e.touches[1]];
    const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    const ratio = dist / p.startDist;
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, p.startZoom * ratio));
    const scale = newZoom / p.startZoom;
    if (innerRef.current) {
      // transform-origin is pinch midpoint relative to the inner container.
      const originX = p.startScrollLeft + p.midX;
      const originY = p.startScrollTop + p.midY;
      innerRef.current.style.transformOrigin = `${originX}px ${originY}px`;
      innerRef.current.style.transform = `scale(${scale})`;
    }
    e.preventDefault();
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const p = pinchRef.current;
    if (!p || !p.active) {
      // Double-tap handler.
      const now = Date.now();
      if (now - lastTapRef.current < 280) {
        setZoom((z) => (z > 1 ? 1 : 1.75));
      }
      lastTapRef.current = now;
      return;
    }
    if (e.touches.length >= 2) return;

    // Compute final zoom from current transform and commit it.
    const inner = innerRef.current;
    let finalZoom = p.startZoom;
    if (inner) {
      const matrix = new DOMMatrix(getComputedStyle(inner).transform);
      const scale = matrix.a || 1;
      finalZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, p.startZoom * scale));
      inner.style.transform = "";
      inner.style.transformOrigin = "0 0";
    }

    // Adjust scroll so the pinch midpoint stays put after we drop the transform
    // and grow the layout via state.
    const scroll = scrollRef.current;
    if (scroll) {
      const k = finalZoom / p.startZoom;
      scroll.scrollLeft = (p.startScrollLeft + p.midX) * k - p.midX;
      scroll.scrollTop = (p.startScrollTop + p.midY) * k - p.midY;
    }

    pinchRef.current = null;
    setZoom(finalZoom);
  }, []);

  const lastTapRef = useRef(0);

  // Live ref of zoom so the (non-React) iOS gesture listeners read it without
  // re-subscribing on every change.
  const zoomRef = useRef(zoom);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);

  // iOS Safari / Despia WebView fire non-standard gesture* events for pinch.
  // Multi-touch `touchmove` is unreliable there and native viewport zoom is
  // disabled, so finger-pinch only works if we drive the zoom from these.
  useEffect(() => {
    const scroll = scrollRef.current;
    if (!scroll || !preferCanvas) return;
    if (typeof window === "undefined" || !("ongesturestart" in window)) return;
    let startZoom = 1;
    const onStart = (e: any) => { e.preventDefault(); startZoom = zoomRef.current; };
    const onChange = (e: any) => {
      e.preventDefault();
      const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, startZoom * (e.scale || 1)));
      setZoom(+next.toFixed(3));
    };
    const onEnd = (e: any) => { e.preventDefault(); };
    scroll.addEventListener("gesturestart", onStart, { passive: false });
    scroll.addEventListener("gesturechange", onChange, { passive: false });
    scroll.addEventListener("gestureend", onEnd, { passive: false });
    return () => {
      scroll.removeEventListener("gesturestart", onStart);
      scroll.removeEventListener("gesturechange", onChange);
      scroll.removeEventListener("gestureend", onEnd);
    };
  }, [preferCanvas, pages.length, imageReady]);

  // ---------- Share / Open external ----------

  const canShareFile = !!payload?.blob;

  const uploadAndSign = useCallback(async (blob: Blob, filename: string): Promise<string | null> => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      if (!userId) return null;
      const safeName = filename.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `${userId}/${Date.now()}/${safeName}`;
      const { error: upErr } = await supabase.storage
        .from("pdf-exports")
        .upload(path, blob, { contentType: "application/pdf", upsert: true });
      if (upErr) return null;
      const { data: signed } = await supabase.storage
        .from("pdf-exports")
        .createSignedUrl(path, 600);
      return signed?.signedUrl ?? null;
    } catch {
      return null;
    }
  }, []);

  const triggerDownload = useCallback((blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* ignore */ } }, 5_000);
    }
  }, []);

  const handleShare = useCallback(async () => {
    if (!payload?.blob) {
      try { window.open(payload?.url, "_blank"); } catch { /* ignore */ }
      return;
    }
    setSharing(true);
    try {
      const file = new File([payload.blob], payload.filename, { type: "application/pdf" });

      // 1. Web Share API with files.
      try {
        const navAny = navigator as Navigator & {
          canShare?: (data: ShareData) => boolean;
          share?: (data: ShareData) => Promise<void>;
        };
        if (navAny.share && (!navAny.canShare || navAny.canShare({ files: [file] }))) {
          await navAny.share({ files: [file], title: payload.filename });
          return;
        }
      } catch (err) {
        const name = (err as { name?: string })?.name;
        if (name === "AbortError" || name === "NotAllowedError") return; // user dismissed
      }

      // 2. Despia native bridge.
      if (isDespiaNative()) {
        const signedUrl = await uploadAndSign(payload.blob, payload.filename);
        if (signedUrl) {
          // Try a save/share-file dialog first, then fall back to file:// (download).
          for (const scheme of [
            `savefiledialog://?url=${encodeURIComponent(signedUrl)}&filename=${encodeURIComponent(payload.filename)}`,
            `file://${signedUrl}`,
          ]) {
            try {
              await despia(scheme as any);
              toast.success(t("pdfViewer.savedToDownloads"));
              return;
            } catch { /* try next scheme */ }
          }
        }
      }

      // 3. Browser download fallback.
      triggerDownload(payload.blob, payload.filename);
      toast.success(t("pdfViewer.savedToDownloads"));
    } catch (err) {
      console.error("[PdfViewerOverlay] share failed:", err);
      toast.error(t("pdfViewer.shareError"));
    } finally {
      setSharing(false);
    }
  }, [payload, uploadAndSign, triggerDownload, t]);

  const zoomLabel = useMemo(() => `${Math.round(zoom * 100)}%`, [zoom]);

  if (!isOpen || !payload) return null;

  const node = (
    <div
      className="fixed inset-0 z-[9999] flex flex-col bg-[#0a0a0a] animate-in fade-in duration-150"
      style={{
        paddingTop: "var(--safe-area-top, env(safe-area-inset-top, 0px))",
        paddingBottom: "var(--safe-bottom)",
      }}

      role="dialog"
      aria-modal="true"
      aria-label={t("pdfViewer.title")}
    >
      {/* Top bar — UNA salida, y donde se busca.
          Antes había dos: una flecha de volver a la izquierda y un «Hecho» de
          texto a la derecha. Dos botones para lo mismo, y el de la derecha (el
          sitio donde se cierra todo lo demás de esta app) no parecía un botón.
          Ahora es una X, redonda, arriba a la derecha. */}
      <div className="flex items-center gap-2 px-3 pt-3 pb-2 shrink-0 text-white">
        {/* Hueco del mismo ancho que la X: mantiene el nombre centrado de verdad. */}
        <div className="w-10 h-10 shrink-0" aria-hidden />

        <div className="flex-1 min-w-0 flex justify-center px-1">
          <div className="px-3 py-1.5 rounded-full bg-white/10 max-w-full">
            <p className="text-white text-[13px] font-semibold truncate text-center leading-tight">
              {payload.filename}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={close}
          className="w-10 h-10 shrink-0 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center active:scale-95 transition-transform"
          aria-label={t("pdfViewer.close")}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Document area */}
      <div className="flex-1 min-h-0 relative bg-neutral-900">
        {preferCanvas ? (
          <>
            <div
              ref={scrollRef}
              className="absolute inset-0 overflow-auto px-2 py-3"
              style={{
                WebkitOverflowScrolling: "touch",
                overscrollBehavior: "contain",
                touchAction: "pan-x pan-y",
              }}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div ref={innerRef} style={{ willChange: "transform" }}>
                {/* LA FOTO. Mismo contenedor que las páginas del PDF, así que
                    hereda el desplazamiento, el pellizco y la píldora de zoom
                    sin repetir nada: aquí el zoom es el ancho, y el navegador
                    la escala desde el original a tamaño completo. */}
                {isImage && (
                  <img
                    src={payload.url}
                    alt={payload.filename}
                    onLoad={() => setImageReady(true)}
                    onError={() =>
                      setRenderError(t("pdfViewer.imageError", { defaultValue: "No se pudo mostrar la imagen" }))
                    }
                    className="block mx-auto my-3 bg-white shadow-2xl rounded-md select-none"
                    style={{ width: `${zoom * 100}%`, maxWidth: "none", height: "auto" }}
                    draggable={false}
                  />
                )}
                {pages.map((meta, idx) => {
                  const pdfPage = pdfPagesRef.current[idx];
                  if (!pdfPage) return null;
                  return (
                    <PdfPageCanvas
                      key={meta.pageNumber}
                      pdfPage={pdfPage}
                      meta={meta}
                      zoom={zoom}
                      dpr={dpr}
                    />
                  );
                })}
              </div>
              {((rendering && pages.length === 0 && !isImage) || (isImage && !imageReady && !renderError)) && (
                <div className="flex flex-col items-center justify-center py-16 gap-3 text-white/70">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  <span className="text-sm">
                    {isImage
                      ? t("pdfViewer.loadingDocument", { defaultValue: "Cargando documento…" })
                      : t("pdfViewer.loading", { defaultValue: "Loading PDF…" })}
                  </span>
                </div>
              )}
              {renderError && (
                <div className="flex items-center justify-center py-16 text-sm text-red-400">
                  {renderError}
                </div>
              )}
            </div>

            {/* Floating zoom pill */}
            {(pages.length > 0 || (isImage && imageReady)) && (
              <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-full bg-black/70 backdrop-blur px-1.5 py-1 text-white shadow-lg">
                <button
                  type="button"
                  onClick={() => setZoom((z) => Math.max(MIN_ZOOM, +(z - 0.25).toFixed(2)))}
                  className="pointer-events-auto w-8 h-8 rounded-full hover:bg-white/10 active:scale-95 flex items-center justify-center"
                  aria-label={t("pdfViewer.zoomOut")}
                >
                  <Minus className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setZoom(1)}
                  className="pointer-events-auto h-8 px-2 rounded-full text-[11px] font-medium hover:bg-white/10 active:scale-95 flex items-center justify-center min-w-[44px]"
                  aria-label={t("pdfViewer.resetZoom")}
                >
                  {zoom === 1 ? <RotateCcw className="w-3.5 h-3.5" /> : zoomLabel}
                </button>
                <button
                  type="button"
                  onClick={() => setZoom((z) => Math.min(MAX_ZOOM, +(z + 0.25).toFixed(2)))}
                  className="pointer-events-auto w-8 h-8 rounded-full hover:bg-white/10 active:scale-95 flex items-center justify-center"
                  aria-label={t("pdfViewer.zoomIn")}
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        ) : (
          <iframe
            key={payload.url}
            src={payload.url}
            title={payload.filename}
            className="absolute inset-0 w-full h-full border-0"
          />
        )}
      </div>

      {/* Bottom action bar */}
      {/* Mismo motivo que en la hoja de voz: esto va pegado al borde de abajo de
          una pantalla completa, así que en Android se comía la barra de botones. */}
      <div className={"shrink-0 text-white px-4 pt-3 flex items-center justify-around gap-2 border-t border-white/5 " + SHEET_SAFE_PB_CLASS}>
        <button
          type="button"
          onClick={handleShare}
          disabled={!canShareFile || sharing}
          className="flex flex-col items-center justify-center gap-1 text-white disabled:opacity-40 disabled:pointer-events-none active:scale-95 transition-transform px-4 py-1"
          aria-label={t("pdfViewer.share")}
        >
          {sharing ? <Loader2 className="w-6 h-6 animate-spin" /> : <IosShareIcon className="w-6 h-6" />}
          <span className="text-[11px] font-medium">{t("pdfViewer.share")}</span>
        </button>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(node, document.body) : node;
}

export default PdfViewerOverlay;
